import React, { useState, useMemo, useEffect, useRef } from "react";
import "@/assets/css/DynamicKanban.scss";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PatientMatchDialog } from "./PatientMatchDialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Building,
  FileText,
  Calendar,
  MoreHorizontal,
  BarChart3,
  AlertTriangle,
  Plus,
  Clock,
  Check,
} from "lucide-react";
import { useNHS111Data } from "@/lib/nhs111/nhs111Data";
import {
  getPatientForNHSEmergencyService,
  recomputeOverdueColumns,
} from "@/dispatchers/pharmacyServiceDispatchers";
import {
  findPharmacyDisposition,
  DISPOSITION_COLOUR_TOKENS,
} from "@/config/iuc/pharmacy-disposition-codes";
import { addPharmacyNotification } from "@/dispatchers/pharmacyNotificationsDispatchers";
import useTitanApp from "@/hooks/useTitanApp";
import { nhs111TemplateId } from "@/data/constantStrings";

type SortKey =
  | "date"
  | "surname"
  | "referrer"
  | "priority"
  | "serviceType"
  | "status";

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: "date", label: "Date / Time" },
  { key: "surname", label: "Patient Surname" },
  { key: "referrer", label: "Referrer" },
  { key: "priority", label: "Priority" },
  { key: "serviceType", label: "Service Type" },
  { key: "status", label: "Status" },
];

const PRIORITY_ORDER: Record<string, number> = {
  high: 1,
  medium: 2,
  low: 3,
};

function sortCards(cards: any[], key: SortKey): any[] {
  return [...cards].sort((a, b) => {
    switch (key) {
      case "surname": {
        const surnameA = (a.patientName ?? "").split(" ").pop() ?? "";
        const surnameB = (b.patientName ?? "").split(" ").pop() ?? "";
        return surnameA.localeCompare(surnameB);
      }
      case "date":
        return (a.date ?? "").localeCompare(b.date ?? "");
      case "referrer":
        return (a.referredFrom ?? a.service ?? "").localeCompare(
          b.referredFrom ?? b.service ?? "",
        );
      case "priority": {
        if (a.dispositionCode || b.dispositionCode) {
          const pa = a.dispositionCode
            ? findPharmacyDisposition(a.dispositionCode)?.priority ?? 99
            : 99;
          const pb = b.dispositionCode
            ? findPharmacyDisposition(b.dispositionCode)?.priority ?? 99
            : 99;
          return pa - pb;
        }
        const prioA = PRIORITY_ORDER[a.priority] ?? 99;
        const prioB = PRIORITY_ORDER[b.priority] ?? 99;
        return prioA - prioB;
      }
      case "serviceType":
        return (a.serviceType ?? a.service ?? "").localeCompare(
          b.serviceType ?? b.service ?? "",
        );
      case "status":
        return (a.consultationStatus ?? "").localeCompare(
          b.consultationStatus ?? "",
        );
      default:
        return 0;
    }
  });
}

function formatOverdue(overdueByMs: number): string {
  const totalMinutes = Math.floor(overdueByMs / (1000 * 60));
  const totalHours = Math.floor(totalMinutes / 60);
  const totalDays = Math.floor(totalHours / 24);

  if (totalDays > 0) {
    const remainingHours = totalHours % 24;
    return `${totalDays}d ${remainingHours}h overdue`;
  }
  if (totalHours > 0) {
    const remainingMinutes = totalMinutes % 60;
    return `${totalHours}h ${remainingMinutes}m overdue`;
  }
  return `${Math.max(1, totalMinutes)}m overdue`;
}

export default function DynamicKanban() {
  const {
    darkMode: isDarkMode,
    NHSEmergencyServiceData,
    NHSEmergencyPatientData,
    formatDate,
    matchDialogOpen,
    setMatchDialogModal,
    handleCardClick,
    handlePatientMatch,
    matchPatient,
    isNHSEmergencyPatientLoading,
    handleAddNewPatient,
  } = useNHS111Data();

  const { appState } = useTitanApp();
  const { currentPharmacyId } = appState || {};

  const [columnSortKeys, setColumnSortKeys] = useState<Record<string, SortKey>>(
    {},
  );
  const notifiedOverdueRef = useRef<Set<string>>(new Set());
  const initialLoadDoneRef = useRef(false);

  const sortedColumns = useMemo(() => {
    return NHSEmergencyServiceData.map((column: any) => {
      const key = columnSortKeys[column.title] ?? "date";
      return {
        ...column,
        data: sortCards(column.data, key),
      };
    });
  }, [NHSEmergencyServiceData, columnSortKeys]);

  const handleSortChange = (columnTitle: string, key: SortKey) => {
    setColumnSortKeys((prev) => ({ ...prev, [columnTitle]: key }));
  };

  useEffect(() => {
    if (!NHSEmergencyServiceData || NHSEmergencyServiceData.length === 0)
      return;

    const allCards = NHSEmergencyServiceData.flatMap(
      (col: any) => col.data || [],
    );
    const currentOverdueIds = new Set(
      allCards
        .filter((c: any) => c.isOverdue)
        .map((c: any) => c.consultationId),
    );

    if (!initialLoadDoneRef.current) {
      currentOverdueIds.forEach((id: string) =>
        notifiedOverdueRef.current.add(id),
      );
      initialLoadDoneRef.current = true;
      return;
    }

    currentOverdueIds.forEach((id: string) => {
      if (!notifiedOverdueRef.current.has(id) && currentPharmacyId) {
        const card = allCards.find((c: any) => c.consultationId === id);
        if (card) {
          addPharmacyNotification({
            pharmacyId: currentPharmacyId,
            notificationStatusType: 3,
            title: "URGENT",
            notificationMessageType: 4,
            entityId: card.consultationId,
          });
          notifiedOverdueRef.current.add(id);
        }
      }
    });
  }, [NHSEmergencyServiceData, currentPharmacyId]);

  useEffect(() => {
    const interval = setInterval(() => {
      recomputeOverdueColumns();
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <PatientMatchDialog
        open={matchDialogOpen}
        onOpenChange={setMatchDialogModal}
        onMatch={handlePatientMatch}
        isDarkMode={isDarkMode}
        matchPatient={matchPatient}
        NHSEmergencyPatientData={NHSEmergencyPatientData}
        isNHSEmergencyPatientLoading={isNHSEmergencyPatientLoading}
        handleAddNewPatient={handleAddNewPatient}
      />

      <div
        className={`h-screen flex flex-col ${
          isDarkMode ? "bg-gray-900" : "bg-gray-50"
        }`}
      >
        {/* Content Area */}
        <div className="flex-1 overflow-auto">
          <div className="flex h-full gap-6">
            {sortedColumns.map((column: any) => (
              <div
                key={column.title}
                className={`flex-1 min-w-80 flex flex-col overflow-y-auto ${
                  isDarkMode ? "bg-gray-800" : "bg-gray-50"
                }border ${isDarkMode ? "border-gray-700" : "border-gray-50"}`}
              >
                {/* Column Header */}
                <div
                  className={`p-4 border-b ${
                    isDarkMode ? "border-gray-700" : "border-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <h3
                        className={`font-semibold ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {column.title}
                      </h3>
                      <Badge
                        variant="secondary"
                        className={`text-xs ${
                          isDarkMode
                            ? "bg-gray-700 text-gray-300"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {column.data.length}
                      </Badge>
                    </div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-6 h-6 p-0"
                        >
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        align="end"
                        className={`w-48 p-1 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700"
                            : "bg-white border-gray-200"
                        }`}
                      >
                        <div
                          className={`px-2 py-1.5 text-xs font-medium ${
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          Sort by
                        </div>
                        {SORT_OPTIONS.map((option) => {
                          const isActive =
                            (columnSortKeys[column.title] ?? "date") ===
                            option.key;
                          return (
                            <button
                              key={option.key}
                              onClick={() =>
                                handleSortChange(column.title, option.key)
                              }
                              className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-sm transition-colors ${
                                isDarkMode
                                  ? "hover:bg-gray-700"
                                  : "hover:bg-gray-100"
                              } ${
                                isActive
                                  ? isDarkMode
                                    ? "font-medium text-blue-400"
                                    : "font-medium text-blue-600"
                                  : isDarkMode
                                  ? "text-gray-300"
                                  : "text-gray-700"
                              }`}
                            >
                              {option.label}
                              {isActive && <Check className="w-3.5 h-3.5" />}
                            </button>
                          );
                        })}
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Column Content */}
                <div className="p-4 space-y-3 overflow-y-auto custom-dyknbn-col">
                  {column.data.map((card: any) => (
                    <div
                      key={card.id}
                      onClick={() => {
                        handleCardClick(card);
                      }}
                      className={`p-3 rounded-lg border cursor-pointer shadow-sm hover:shadow-md transition-all duration-200 ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600 hover:bg-gray-600"
                          : "bg-white border-gray-50 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {(() => {
                            const disposition = card.dispositionCode
                              ? findPharmacyDisposition(card.dispositionCode)
                              : undefined;
                            if (disposition) {
                              const dotClass =
                                DISPOSITION_COLOUR_TOKENS[disposition.colour]
                                  .dot;
                              return (
                                <div className="group relative">
                                  <div
                                    className={`w-2 h-2 rounded-full flex-shrink-0 ${dotClass}`}
                                    title={`${disposition.code} · ${disposition.text}`}
                                  />
                                </div>
                              );
                            }
                            if (card.priority) {
                              return (
                                <div
                                  className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                    card.priority === "high"
                                      ? "bg-red-500"
                                      : card.priority === "medium"
                                      ? "bg-orange-500"
                                      : "bg-green-500"
                                  }`}
                                />
                              );
                            }
                            return null;
                          })()}
                          <h4
                            className={`text-sm font-medium truncate ${
                              isDarkMode ? "text-white" : "text-gray-900"
                            }`}
                          >
                            {card.patientName}
                          </h4>
                        </div>
                        <div className="flex items-center flex-shrink-0 gap-1 ml-2">
                          <Clock className="w-3 h-3 text-gray-400" />
                          <span
                            className={`text-xs ${
                              isDarkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            {formatDate(card.date)}
                          </span>
                        </div>
                      </div>

                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        {/* {card.referredFrom && (
                          <div
                            className={`px-1.5 py-0.5 rounded text-xs ${
                              card.referredFrom === "GP Practice"
                                ? isDarkMode
                                  ? "bg-green-900/30 text-green-300"
                                  : "bg-green-100 text-green-700"
                                : isDarkMode
                                ? "bg-blue-900/30 text-blue-300"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {card.referredFrom}
                          </div>
                        )} */}

                        {card.templateId && (
                          <div
                            className={`px-1.5 py-0.5 rounded text-xs ${
                              card.templateId === nhs111TemplateId
                                ? isDarkMode
                                  ? "bg-green-900/30 text-green-300"
                                  : "bg-green-100 text-green-700"
                                : isDarkMode
                                ? "bg-blue-900/30 text-blue-300"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {card.templateId === nhs111TemplateId
                              ? "NHS 111"
                              : "GP Practice"}
                          </div>
                        )}
                        {card.service && (
                          <div
                            className={`px-1.5 py-0.5 rounded text-xs ${
                              isDarkMode
                                ? "bg-blue-900/30 text-blue-300"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {card.service}{" "}
                            {card.pathwaysDisposition &&
                              `/ ${card.pathwaysDisposition}`}
                          </div>
                        )}

                        {/* {card.pathwaysDisposition && (
                          <div
                            className={`px-1.5 py-0.5 rounded text-xs ${
                              isDarkMode
                                ? "bg-teal-900/30 text-teal-300"
                                : "bg-teal-100 text-teal-700"
                            }`}
                          >
                            {card.pathwaysDisposition}
                          </div>
                        )} */}
                      </div>

                      {(card?.dob || card?.address) && (
                        <div className="mt-2 space-y-1 text-xs">
                          <div
                            className={
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }
                          >
                            <span className="font-medium">DOB</span>{" "}
                            <span>{card?.dob || "—"}</span>
                          </div>

                          <div
                            className={`break-words ${
                              isDarkMode ? "text-gray-400" : "text-gray-600"
                            }`}
                          >
                            {card?.address || "—"}
                          </div>
                        </div>
                      )}
                      {card.isOverdue && card.overdueByMs > 0 && (
                        <div
                          className={`flex items-center gap-1.5 mt-1.5 py-1 px-2 rounded border text-xs font-medium ${
                            isDarkMode
                              ? "bg-red-900/20 border-red-800/50 text-red-300"
                              : "bg-red-50 border-red-200 text-red-700"
                          }`}
                        >
                          <AlertTriangle className="flex-shrink-0 w-3 h-3" />
                          {formatOverdue(card.overdueByMs)}
                        </div>
                      )}

                      {/* Card Content */}
                      <div className="mt-3 space-y-2">
                        {/* Warning for unmatched patients */}
                        {card.serviceType === "nhs" &&
                          card.hasLocalRecord === false && (
                            <div
                              className={`flex items-center gap-2 py-2 px-3 rounded-md border ${
                                isDarkMode
                                  ? "bg-red-900/20 border-red-800/50 text-red-300"
                                  : "bg-red-50 border-red-200 text-red-800"
                              }`}
                            >
                              <AlertTriangle className="flex-shrink-0 w-3 h-3" />
                              <span className="text-xs">
                                Patient not matched
                              </span>
                            </div>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
